import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { format } from "date-fns";
import ManagerNavbar from "../Layout/ManagerNavbar";
import API_BASE_URL from "../config";

/* ─── Inline styles & keyframes injected once ─── */
const styleSheet = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@300;400;500;600&display=swap');

  :root {
    --navy:   #0f172a;
    --slate:  #1e293b;
    --card:   #ffffff;
    --border: #e2e8f0;
    --muted:  #64748b;
    --accent: #f59e0b;
    --accent2:#ef4444;
    --green:  #10b981;
    --blue:   #3b82f6;
    --purple: #8b5cf6;
    --text:   #0f172a;
    --bg:     #f1f5f9;
    --radius: 16px;
    --shadow: 0 4px 24px rgba(15,23,42,0.08);
    --shadow-hover: 0 12px 40px rgba(15,23,42,0.14);
  }

  .ehp-wrapper * { box-sizing: border-box; font-family: 'DM Sans', sans-serif; }

  .ehp-wrapper {
    min-height: 100vh;
    background: var(--bg);
    padding-top: 80px;
  }

  /* ── Page header ── */
  .ehp-header {
    background: var(--navy);
    padding: 32px 40px 28px;
    position: relative;
    overflow: hidden;
  }
  .ehp-header::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse at 80% 50%, rgba(245,158,11,0.18) 0%, transparent 70%),
                radial-gradient(ellipse at 10% 80%, rgba(59,130,246,0.12) 0%, transparent 60%);
  }
  .ehp-header-inner {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
  }
  .ehp-title-block {}
  .ehp-eyebrow {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 3px;
    text-transform: uppercase;
    color: var(--accent);
    margin-bottom: 6px;
  }
  .ehp-title {
    font-family: 'Playfair Display', serif;
    font-size: 30px;
    font-weight: 700;
    color: #fff;
    margin: 0;
    line-height: 1.15;
  }
  .ehp-title span { color: var(--accent); }

  /* ── Controls ── */
  .ehp-controls {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
  }
  .ehp-select {
    background: rgba(255,255,255,0.1);
    border: 1px solid rgba(255,255,255,0.2);
    color: #fff;
    border-radius: 10px;
    padding: 8px 14px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;
    appearance: none;
    -webkit-appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23fff' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    padding-right: 32px;
    outline: none;
  }
  .ehp-select:hover { background-color: rgba(255,255,255,0.18); }
  .ehp-select option { background: var(--navy); color: #fff; }

  .ehp-refresh-btn {
    background: var(--accent);
    border: none;
    border-radius: 10px;
    padding: 8px 20px;
    font-size: 13px;
    font-weight: 600;
    color: var(--navy);
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: transform 0.15s, box-shadow 0.15s;
  }
  .ehp-refresh-btn:hover { transform: translateY(-1px); box-shadow: 0 4px 14px rgba(245,158,11,0.4); }
  .ehp-refresh-btn:active { transform: translateY(0); }

  /* ── Stats bar ── */
  .ehp-stats {
    display: flex;
    gap: 12px;
    padding: 16px 40px;
    background: var(--card);
    border-bottom: 1px solid var(--border);
    flex-wrap: wrap;
  }
  .ehp-stat {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--muted);
    font-weight: 500;
  }
  .ehp-stat-dot {
    width: 10px; height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .ehp-stat-count { font-weight: 700; color: var(--text); }

  /* ── Content area ── */
  .ehp-content {
    padding: 28px 40px 40px;
  }

  /* ── Month group label ── */
  .ehp-month-label {
    font-family: 'Playfair Display', serif;
    font-size: 20px;
    font-weight: 600;
    color: var(--text);
    margin: 0 0 16px;
    padding-bottom: 10px;
    border-bottom: 2px solid var(--border);
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .ehp-month-label::after {
    content: '';
    flex: 1;
    height: 2px;
    background: linear-gradient(to right, var(--border), transparent);
  }

  /* ── Holiday card grid ── */
  .ehp-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 16px;
    margin-bottom: 32px;
  }

  /* ── Holiday card ── */
  .ehp-card {
    background: var(--card);
    border-radius: var(--radius);
    border: 1px solid var(--border);
    padding: 20px 22px;
    box-shadow: var(--shadow);
    transition: transform 0.22s cubic-bezier(.22,.68,0,1.2), box-shadow 0.22s ease;
    display: flex;
    flex-direction: column;
    gap: 12px;
    position: relative;
    overflow: hidden;
    animation: cardIn 0.35s ease both;
  }
  .ehp-card:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-hover);
  }
  .ehp-card-accent {
    position: absolute;
    top: 0; left: 0;
    width: 4px;
    height: 100%;
    border-radius: 4px 0 0 4px;
  }
  .ehp-card-accent.public  { background: var(--green); }
  .ehp-card-accent.optional{ background: var(--accent); }
  .ehp-card-accent.other   { background: var(--purple); }

  .ehp-card-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
  }
  .ehp-card-name {
    font-size: 15px;
    font-weight: 600;
    color: var(--text);
    line-height: 1.3;
    flex: 1;
  }

  .ehp-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .ehp-badge.public   { background: #d1fae5; color: #065f46; }
  .ehp-badge.optional { background: #fef3c7; color: #92400e; }
  .ehp-badge.other    { background: #ede9fe; color: #4c1d95; }

  .ehp-card-dates {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  .ehp-date-chip {
    display: flex;
    align-items: center;
    gap: 5px;
    background: var(--bg);
    border-radius: 8px;
    padding: 5px 10px;
    font-size: 12px;
    color: var(--muted);
    font-weight: 500;
  }
  .ehp-date-chip svg { opacity: 0.6; flex-shrink: 0; }
  .ehp-date-chip strong { color: var(--text); font-weight: 600; }

  .ehp-card-desc {
    font-size: 13px;
    color: var(--muted);
    line-height: 1.5;
    margin: 0;
    border-top: 1px solid var(--border);
    padding-top: 10px;
  }

  .ehp-card-number {
    position: absolute;
    bottom: 16px; right: 18px;
    font-size: 42px;
    font-weight: 700;
    font-family: 'Playfair Display', serif;
    color: var(--border);
    line-height: 1;
    user-select: none;
  }

  /* ── Empty / loading state ── */
  .ehp-empty {
    text-align: center;
    padding: 80px 20px;
    color: var(--muted);
  }
  .ehp-empty-icon { font-size: 56px; margin-bottom: 16px; opacity: 0.4; }
  .ehp-empty h5 { font-size: 18px; font-weight: 600; color: var(--text); margin: 0 0 6px; }
  .ehp-empty p  { font-size: 14px; margin: 0; }

  .ehp-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 14px;
    padding: 80px 20px;
    color: var(--muted);
    font-size: 14px;
  }
  .ehp-spinner {
    width: 28px; height: 28px;
    border: 3px solid var(--border);
    border-top-color: var(--accent);
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }

  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes cardIn {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* ── Responsive ── */
  @media (max-width: 640px) {
    .ehp-header { padding: 24px 20px 20px; }
    .ehp-stats  { padding: 12px 20px; }
    .ehp-content{ padding: 20px 16px 32px; }
    .ehp-title  { font-size: 22px; }
    .ehp-grid   { grid-template-columns: 1fr; }
  }
`;

/* ─── Helper icons ─── */
const CalendarIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="3" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.4"/>
    <path d="M5 1v4M11 1v4M1 7h14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);
const RefreshIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 8A6 6 0 1 1 8 2a6 6 0 0 1 4.243 1.757L14 2v4h-4l1.5-1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/* ─── Type helpers ─── */
const typeKey = (type) => {
  const t = (type || "").toUpperCase();
  if (t === "PUBLIC")   return "public";
  if (t === "OPTIONAL") return "optional";
  return "other";
};
const typeLabel = (type) => {
  if (!type) return "N/A";
  const t = type.toUpperCase();
  if (t === "PUBLIC")   return "🟢 Public";
  if (t === "OPTIONAL") return "🟡 Optional";
  return type;
};

/* ─── Group holidays by month (chronological order) ─── */
const groupByMonth = (holidays) => {
  // Sort all holidays by startDate ascending first
  const sorted = [...holidays].sort(
    (a, b) => new Date(a.startDate) - new Date(b.startDate)
  );

  // Build an ordered Map so insertion order = chronological order
  const groups = new Map();
  sorted.forEach((h) => {
    const d = new Date(h.startDate);
    const key = isNaN(d) ? "Unknown" : format(d, "MMMM yyyy");
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(h);
  });

  // Return as plain array of [label, items] pairs (already sorted)
  return Array.from(groups.entries());
};

/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════ */
const ManagerHolidayPage = () => {
  const currentDate = new Date();
  const [holidays, setHolidays]   = useState([]);
  const [month, setMonth]         = useState(currentDate.getMonth() + 1);
  const [year, setYear]           = useState(currentDate.getFullYear());
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");
  const navigate = useNavigate();

  const token = localStorage.getItem("token");

  /* Inject styles once */
  useEffect(() => {
    const id = "ehp-styles";
    if (!document.getElementById(id)) {
      const el = document.createElement("style");
      el.id = id;
      el.textContent = styleSheet;
      document.head.appendChild(el);
    }
  }, []);

  /* Auth check */
  useEffect(() => {
    if (!token) {
      toast.error("You must be logged in to access this page.");
      navigate("/");
    }
  }, [navigate]);

  /* Fetch on change */
  useEffect(() => {
    fetchHolidays(year, month);
  }, [month, year]);

  const fetchHolidays = async (y, m) => {
    try {
      setLoading(true);
      setError("");
      const res = await axios.get(`${API_BASE_URL}/api/holidays`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      let all = res.data || [];
      all = all.filter((h) => new Date(h.startDate).getFullYear() === y);
      if (m !== 0) {
        all = all.filter((h) => new Date(h.startDate).getMonth() + 1 === m);
      }
      setHolidays(all);
    } catch (err) {
      console.error("❌ Failed to fetch holidays:", err);
      setError("Failed to fetch holidays. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const monthNames = [
    "All Months","January","February","March","April","May","June",
    "July","August","September","October","November","December",
  ];

  /* Stats */
  const publicCount   = holidays.filter((h) => h.type?.toUpperCase() === "PUBLIC").length;
  const optionalCount = holidays.filter((h) => h.type?.toUpperCase() === "OPTIONAL").length;
  const otherCount    = holidays.length - publicCount - optionalCount;

  /* Grouped */
  const groups = groupByMonth(holidays);

  return (
    <>
      <div className="fixed-top">
        <ManagerNavbar />
      </div>

      <div className="ehp-wrapper">
        {/* ── Header ── */}
        <div className="ehp-header">
          <div className="ehp-header-inner">
            <div className="ehp-title-block">
              <div className="ehp-eyebrow">Company Calendar</div>
              <h1 className="ehp-title">Holiday <span>Schedule</span></h1>
            </div>

            <div className="ehp-controls">
              <select
                className="ehp-select"
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value))}
              >
                {monthNames.map((m, i) => (
                  <option key={i} value={i === 0 ? 0 : i}>{m}</option>
                ))}
              </select>

              <select
                className="ehp-select"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
              >
                {Array.from({ length: 5 }, (_, i) => year - 2 + i).map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>

              <button className="ehp-refresh-btn" onClick={() => fetchHolidays(year, month)}>
                <RefreshIcon /> Refresh
              </button>
            </div>
          </div>
        </div>

        {/* ── Stats bar ── */}
        {!loading && !error && holidays.length > 0 && (
          <div className="ehp-stats">
            <div className="ehp-stat">
              <div className="ehp-stat-dot" style={{ background: "#10b981" }} />
              <span className="ehp-stat-count">{publicCount}</span> Public Holiday{publicCount !== 1 ? "s" : ""}
            </div>
            {optionalCount > 0 && (
              <div className="ehp-stat">
                <div className="ehp-stat-dot" style={{ background: "#f59e0b" }} />
                <span className="ehp-stat-count">{optionalCount}</span> Optional
              </div>
            )}
            {otherCount > 0 && (
              <div className="ehp-stat">
                <div className="ehp-stat-dot" style={{ background: "#8b5cf6" }} />
                <span className="ehp-stat-count">{otherCount}</span> Other
              </div>
            )}
            <div className="ehp-stat" style={{ marginLeft: "auto" }}>
              <span className="ehp-stat-count">{holidays.length}</span> total
            </div>
          </div>
        )}

        {/* ── Content ── */}
        <div className="ehp-content">
          {loading ? (
            <div className="ehp-loading">
              <div className="ehp-spinner" />
              Loading holidays…
            </div>
          ) : error ? (
            <div className="ehp-empty">
              <div className="ehp-empty-icon">⚠️</div>
              <h5>Something went wrong</h5>
              <p>{error}</p>
            </div>
          ) : holidays.length === 0 ? (
            <div className="ehp-empty">
              <div className="ehp-empty-icon">🗓️</div>
              <h5>No holidays found</h5>
              <p>Try selecting a different month or year.</p>
            </div>
          ) : (
            groups.map(([monthLabel, items]) => (
              <div key={monthLabel}>
                <div className="ehp-month-label">{monthLabel}</div>
                <div className="ehp-grid">
                  {items.map((h, i) => {
                    const start = new Date(h.startDate);
                    const end   = h.endDate ? new Date(h.endDate) : null;
                    const startStr = !isNaN(start) ? format(start, "dd MMM yyyy (EEE)") : "—";
                    const endStr   = end && !isNaN(end) ? format(end, "dd MMM yyyy (EEE)") : null;
                    const tk = typeKey(h.type);

                    return (
                      <div
                        className="ehp-card"
                        key={i}
                        style={{ animationDelay: `${i * 50}ms` }}
                      >
                        <div className={`ehp-card-accent ${tk}`} />

                        <div className="ehp-card-top">
                          <div className="ehp-card-name">{h.name || "—"}</div>
                          <span className={`ehp-badge ${tk}`}>
                            {typeLabel(h.type)}
                          </span>
                        </div>

                        <div className="ehp-card-dates">
                          <div className="ehp-date-chip">
                            <CalendarIcon />
                            <strong>{startStr}</strong>
                          </div>
                          {endStr && (
                            <div className="ehp-date-chip">
                              <CalendarIcon />
                              <span>to&nbsp;<strong>{endStr}</strong></span>
                            </div>
                          )}
                        </div>

                        {h.description && (
                          <p className="ehp-card-desc">{h.description}</p>
                        )}

                        <div className="ehp-card-number">{String(i + 1).padStart(2, "0")}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default ManagerHolidayPage;